import { IFileArchiveFile } from '@/shared/api-client/models/file-archive-file';
import { IFileBucketFile } from '@/shared/api-client/models/file-bucket-file';
import { friendlyDateTime, friendlyFileSize } from '@/shared/filters';
import { ArgumentValidator, ConsoleFacade } from '@/shared/helpers';
import { Urls } from '@/shared/helpers/urls';
import { IFileFormatConfigSpecification } from '@/shared/models/file-format-config-specification';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import FileUploadComponent from '../file-upload-component/file-upload-component.vue';

interface IViewModel {

    loading: boolean;
    showFileUpload: boolean;
    files: IFileArchiveFile[];
    newBucketFileIds: string[];
    deletedArchiveFileIds: string[];
    recentUploadedFileIds: string[];
}

export interface IFileListChanged {

    files: IFileArchiveFile[]
    newBucketFileIds: string[],
    deletedArchiveFileIds: string[]
}

@Component({
    components: {
        FileUploadComponent
    },
    filters: {
        friendlyDateTime,
        friendlyFileSize
    }
})
export default class FileArchiveComponent extends Vue {

    @Prop() public files!: IFileArchiveFile[];
    @Prop() public translations!: { [key: string]: string; };
    @Prop({default: () => []}) public restrictedToContentTypes!: IFileFormatConfigSpecification[];
    @Prop({default: false}) public isReadonly!: boolean;

    public viewModel: IViewModel = {
        loading: false,
        showFileUpload: false,
        files: [],
        deletedArchiveFileIds: [],
        newBucketFileIds: [],
        recentUploadedFileIds: []
    };

    @Emit('addFileClicked')
    // tslint:disable-next-line: no-empty
    public addFileClicked(): void { }

    @Emit('fileListChanged')
    public emitFileListChanged(): IFileListChanged {

        return {
            files: this.viewModel.files,
            newBucketFileIds: this.viewModel.newBucketFileIds,
            deletedArchiveFileIds: this.viewModel.deletedArchiveFileIds
        }
    }

    public async created(): Promise<void> {

        ConsoleFacade.log('FileArchiveComponent created.');

        this.viewModel.files = this.files || [];
    }

    public getArchiveFileIds(): string {

        return this.viewModel.files.map(f => f.id).join(',');
    }

    public getNewBucketFileIds(): string {

        return this.viewModel.newBucketFileIds.map(f => f).join(',');
    }

    public getDeletedArchiveFileIds(): string {

        return this.viewModel.deletedArchiveFileIds.map(f => f).join(',');
    }

    public remove(id: string, $event?: MouseEvent): void {

        if ($event) {

            $event.preventDefault();
        }

        if (this.isReadonly) {
            return;
        }

        const file = this.viewModel.files.find(f => f.id === id);

        if (file) {

            this.viewModel.newBucketFileIds = this.viewModel.newBucketFileIds.filter(newId => newId !== file.id);
        }

        this.viewModel.files = this.viewModel.files.filter(f => f.id !== id);

        if (!id.startsWith('tmp')) {

            this.viewModel.deletedArchiveFileIds.push(id);
        }

        this.emitFileListChanged();
    }

    public fileUploaded(args: { fileBucketFile: IFileBucketFile, file: File }): void {

        const fileBucketFile = args.fileBucketFile;

        const fileArchiveFile: IFileArchiveFile = {
            id: `tmp/file-${fileBucketFile.id}`,
            fileId: fileBucketFile.id,
            fileName: fileBucketFile.fileName,
            created: fileBucketFile.created,
            size: fileBucketFile.size
        };

        this.viewModel.files.push(fileArchiveFile);

        this.viewModel.recentUploadedFileIds.push(fileArchiveFile.id);

        this.viewModel.newBucketFileIds.push(fileArchiveFile.fileId);

        this.toggleFileUpload();

        this.emitFileListChanged();
    }

    public isRecentUploaded(file: IFileArchiveFile): boolean {

        ArgumentValidator.notNullOrEmpty(file, `file`);

        return this.viewModel.recentUploadedFileIds.findIndex(x => x === file.id) !== -1;
    }

    public toggleFileUpload($event?: MouseEvent): void {

        if ($event) {

            $event.preventDefault();
        }

        if (this.isReadonly) {
            return;
        }

        if (this.viewModel.showFileUpload) {

            this.viewModel.showFileUpload = false;
        } else {
            this.viewModel.showFileUpload = true;
        }

        this.addFileClicked();
    }

    public async download(file: IFileArchiveFile, $event?: MouseEvent) {

        ArgumentValidator.notNullOrEmpty(file, `file`);

        if ($event) {

            $event.preventDefault();
        }

        window.location.href = Urls.fileArchiveDownloadFile(file.id);
    }
}
